import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Separator,
	Skeleton,
} from "@ncoder/ui";
import { AlertTriangle, BarChart2, ChevronDown, ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { type CoverageCategory, type CoverageDashboard, fetchCoverageDashboard } from "@/services/testing";

/**
 * Declarative coverage dashboard: the scorecard Salesforce never gives you — % of your objects /
 * validation rules / flows with at least one passing test, plus the uncovered items (each with a
 * one-click "test this"). Backed by GET /api/plans/coverage/dashboard.
 */
export function CoverageDashboardDialog({
	onUseInChat,
	trigger,
}: {
	onUseInChat: (text: string) => void;
	/** Callers needing a different affordance (e.g. a sidebar row) supply their own trigger. */
	trigger?: ReactNode;
}) {
	const [open, setOpen] = useState(false);
	const [data, setData] = useState<CoverageDashboard | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open || data) return;
		fetchCoverageDashboard()
			.then((d) => {
				setData(d);
				setError(null);
			})
			.catch((e) => setError(e instanceof Error ? e.message : "Couldn't load coverage."));
	}, [open, data]);

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				{trigger ?? (
					<Button variant="outline" size="sm" className="gap-1.5 text-xs">
						<BarChart2 className="h-3.5 w-3.5" />
						Coverage
					</Button>
				)}
			</DialogTrigger>
			<DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
				<DialogHeader className="space-y-1.5 border-b px-5 py-4">
					<DialogTitle className="flex items-center gap-2 text-base">
						<BarChart2 className="h-4 w-4 text-primary" />
						Test coverage
					</DialogTitle>
					<DialogDescription className="text-xs leading-relaxed">
						What share of your objects, validation rules, and flows has at least one passing test —
						the declarative coverage Salesforce's 75%-Apex metric can't see.
					</DialogDescription>
				</DialogHeader>

				<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
					{error ? (
						<p className="flex items-start gap-1.5 text-xs text-destructive">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							{error}
						</p>
					) : !data ? (
						<div className="space-y-3">
							<Skeleton className="h-16 w-full rounded-lg" />
							{[0, 1, 2].map((i) => (
								<Skeleton key={i} className="h-12 w-full rounded-lg" />
							))}
						</div>
					) : (
						<>
							<div className="rounded-lg border bg-muted/30 p-4 text-center">
								<div className="text-3xl font-semibold">{data.overallPct}%</div>
								<div className="mt-0.5 text-xs text-muted-foreground">
									of your testable surface has a passing test
								</div>
							</div>
							{data.categories.map((c) => (
								<CategoryRow key={c.key} cat={c} onUseInChat={onUseInChat} />
							))}
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

function barColor(pct: number): string {
	if (pct >= 80) return "bg-success";
	if (pct >= 40) return "bg-warning";
	return "bg-destructive";
}

function CategoryRow({
	cat,
	onUseInChat,
}: {
	cat: CoverageCategory;
	onUseInChat: (text: string) => void;
}) {
	const [expanded, setExpanded] = useState(false);
	if (!cat.available) {
		// We couldn't READ this category — never show a misleading % here.
		return (
			<div className="rounded-lg border border-dashed p-3">
				<div className="flex items-center justify-between gap-2">
					<span className="text-sm font-medium">{cat.label}</span>
					<span className="text-xs text-muted-foreground">unavailable</span>
				</div>
				<p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-muted-foreground">
					<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
					Couldn't read {cat.label.toLowerCase()} from your org (permissions or API access) — this
					isn't 0% covered, it's unknown. Grant read access and re-open.
				</p>
			</div>
		);
	}
	return (
		<div className="rounded-lg border p-3">
			<div className="flex items-center justify-between gap-2">
				<span className="text-sm font-medium">{cat.label}</span>
				<span className="text-xs text-muted-foreground">
					{cat.covered}/{cat.total} · <span className="font-medium text-foreground">{cat.pct}%</span>
				</span>
			</div>
			<div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
				<div className={`h-full ${barColor(cat.pct)}`} style={{ width: `${cat.pct}%` }} />
			</div>
			{cat.uncovered.length > 0 && (
				<>
					<button
						type="button"
						onClick={() => setExpanded((e) => !e)}
						className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
					>
						{expanded ? (
							<ChevronDown className="h-3.5 w-3.5" />
						) : (
							<ChevronRight className="h-3.5 w-3.5" />
						)}
						{cat.uncovered.length} untested {cat.label.toLowerCase()}
					</button>
					{expanded && (
						<div className="mt-1.5 space-y-1">
							{cat.uncovered.slice(0, 50).map((name) => (
								<div
									key={name}
									className="flex items-center justify-between gap-2 rounded-md border bg-card/50 px-2.5 py-1.5"
								>
									<span className="truncate font-mono text-[11px]">{name}</span>
									<Button
										variant="ghost"
										size="sm"
										className="h-6 shrink-0 px-2 text-[11px]"
										onClick={() => onUseInChat(useInChatPrompt(cat.key, name))}
									>
										Test this
									</Button>
								</div>
							))}
							{cat.uncovered.length > 50 && (
								<p className="px-1 text-[11px] text-muted-foreground">
									+{cat.uncovered.length - 50} more…
								</p>
							)}
						</div>
					)}
				</>
			)}
			{cat.total === 0 && (
				<>
					<Separator className="my-2" />
					<p className="text-[11px] text-muted-foreground">None found in this org.</p>
				</>
			)}
		</div>
	);
}

function useInChatPrompt(key: string, name: string): string {
	if (key === "validation_rules") {
		const [obj, rule] = name.split(".");
		return `Write a test that triggers the "${rule}" validation rule on ${obj} and asserts the error appears on save.`;
	}
	if (key === "flows") {
		return `Write a UI test that walks the ${name} flow end-to-end and verifies its result via SOQL.`;
	}
	return `Write a test that exercises the ${name} object and verifies a record via SOQL.`;
}
